import React, { useMemo, useState } from 'react';
import { GivelifyCheckbox, GivelifyLabel } from '@givelify/ui';
import { useTrackingContext } from '@givelify/utils';
import { SignInRequest } from 'api/requests/auth';
import { useTranslation } from 'react-i18next';
import { LOGIN_PAGE_CLICK_SHOW_PASS } from 'utils/clevertapEvents';
import { webConfig } from 'webConfig';
import { useLoginContext } from '../LoginContext';
import {
    ActionRow,
    CheckBoxContent,
    CreateAccountRow,
    Form,
    FormTextField,
    NotVerifiedList,
    NotVerifiedSupportLink,
    OrangeLink,
    SignInButton,
    TextFieldWrapper,
} from './styles';
import { useLoginFormText } from './useLoginFormText';

interface LoginFormProps {
    handleResetPasswordOpen?: () => void;
}

const NotVerifiedWarning = () => {
    const { t } = useTranslation();

    const copy = useMemo(
        () => ({
            toSignIn: t('login.notVerified.toSignIn'),
            checkEmail: t('login.notVerified.checkEmail'),
            click: t('login.notVerified.click'),
            help: t('login.notVerified.help'),
            link: t('login.notVerified.link'),
        }),
        [t],
    );

    return (
        <>
            <GivelifyLabel
                color="secondary"
                text={copy.toSignIn}
                variant="body2"
            />
            <NotVerifiedList>
                <li>{copy.checkEmail}</li>
                <li>{copy.click}</li>
                <li>
                    {copy.help}{' '}
                    <NotVerifiedSupportLink
                        href={webConfig.supportUrl}
                        name="Email Not Verified Support"
                        target="_blank"
                        text={copy.link}
                    />
                </li>
            </NotVerifiedList>
        </>
    );
};

const LoginForm: React.FCC<LoginFormProps> = (props) => {
    const { trackEvent } = useTrackingContext();
    const [passwordVisibility, setPasswordVisibility] = useState(false);

    const loginFormText = useLoginFormText();

    const { formError, isValid, submitForm, isLoading, showNotVerified } =
        useLoginContext();

    const handlePasswordVisibility = () => {
        trackEvent(LOGIN_PAGE_CLICK_SHOW_PASS);
        setPasswordVisibility((val) => !val);
    };

    return (
        <Form onSubmit={submitForm}>
            <GivelifyLabel
                color="secondary"
                id="error-details"
                style={{
                    marginBottom: 8,
                }}
            >
                {formError}
            </GivelifyLabel>
            {showNotVerified && <NotVerifiedWarning />}
            <TextFieldWrapper>
                <FormTextField<SignInRequest>
                    fullWidth
                    aria-label="Username input"
                    id="username-input"
                    label={loginFormText.title.email}
                    name="username"
                    placeholder={loginFormText.title.email}
                    size="large"
                    variant="outlined"
                />
            </TextFieldWrapper>
            <TextFieldWrapper>
                <FormTextField<SignInRequest>
                    fullWidth
                    aria-label="Password input"
                    id="password-input"
                    label={loginFormText.title.password}
                    name="password"
                    placeholder={loginFormText.title.passwordHolder}
                    size="large"
                    type={passwordVisibility ? 'text' : 'password'}
                />
            </TextFieldWrapper>
            <ActionRow>
                <CheckBoxContent>
                    <GivelifyCheckbox
                        aria-label="login-show-password-checkbox"
                        checked={passwordVisibility}
                        color="default"
                        name="login-show-password-checkbox"
                        onChange={handlePasswordVisibility}
                        style={{
                            backgroundColor: 'transparent',
                        }}
                    />
                    <GivelifyLabel
                        bold
                        color="GrayText"
                        text={loginFormText.text.showPassword}
                        variant="body2"
                    />
                </CheckBoxContent>
                <OrangeLink
                    name="Forgot Pass"
                    onClick={props.handleResetPasswordOpen}
                    sx={{
                        fontWeight: 700,
                    }}
                    text={loginFormText.link.forgotPassword}
                />
            </ActionRow>
            <SignInButton
                fullWidth
                disabled={!isValid}
                isLoading={isLoading}
                size="large"
                text={loginFormText.title.signIn}
                type="submit"
                variant="primary"
            />
            <CreateAccountRow>
                <GivelifyLabel
                    style={{
                        display: 'inline-block',
                    }}
                    text={loginFormText.text.newToGivelify}
                    variant="body1"
                />
                <OrangeLink
                    href={webConfig.loginPageSignupUrl}
                    name="Sign Up"
                    sx={{
                        marginLeft: '8px',
                    }}
                    target="_blank"
                    text={`${loginFormText.link.signUp}.`}
                    variant="body1"
                />
            </CreateAccountRow>
        </Form>
    );
};

export default LoginForm;
